/**
 * SearchBar component - Airbnb/Booking.com style editable search bar
 *
 * Displays current search criteria and allows editing in a modal or inline.
 * Uses useLocations hook for proper API data fetching.
 */

import { useState, useRef, useEffect } from "react";
import { MapPin, Calendar, Users, Search, ChevronDown, X } from "lucide-react";
import { observer } from "mobx-react-lite";
import { Select, DatePicker } from "@/shared/ui/components";
import { useGeoLocations } from "@/features/geo/hooks";
import { useStore } from "@/stores";
import { OCCUPANT_OPTIONS } from "@/shared/constants/locations";
import { Box, Text } from "@/shared/ui/base";

export interface SearchCriteria {
  prefecture: string;
  city: string;
  ward: string;
  moveInDate: Date | null;
  occupants: number;
}

interface SearchBarProps {
  criteria: SearchCriteria;
  onChange: (criteria: SearchCriteria) => void;
  className?: string;
}

export const SearchBar = observer(function SearchBar({
  criteria,
  onChange,
  className = "",
}: SearchBarProps) {
  const { uiStore } = useStore();
  const t = uiStore.t;
  const language = uiStore.locale;
  const [isExpanded, setIsExpanded] = useState(false);
  const [localCriteria, setLocalCriteria] = useState(criteria);
  const containerRef = useRef<HTMLDivElement>(null);

  // Internal state for managing API dependencies
  const [selectedPrefectureCode, setSelectedPrefectureCode] = useState<
    number | undefined
  >(undefined);
  const [selectedCitySlug, setSelectedCitySlug] = useState<string | undefined>(
    undefined,
  );

  // Fetch location data from API using useGeoLocations hook
  const { prefectures, cities, locations } = useGeoLocations({
    lang: language,
    prefectureCode: selectedPrefectureCode,
    citySlug: selectedCitySlug,
  });

  // Ensure arrays are always defined (defensive check for MobX state)
  const safePrefectures = Array.isArray(prefectures) ? prefectures : [];
  const safeCities = Array.isArray(cities) ? cities : [];
  const safeLocations = Array.isArray(locations) ? locations : [];

  // Convert API data to CustomSelect options format
  const prefectureOptions = safePrefectures.map((p) => ({
    value: p.code,
    label:
      language === "ja"
        ? p.name.ja
        : language === "ru"
          ? p.name.ru || p.name.en
          : p.name.en,
  }));

  const cityOptions = safeCities.map((c) => ({
    value: c.city_slug,
    label:
      language === "ja"
        ? c.name.ja
        : language === "ru"
          ? c.name.ru || c.name.en
          : c.name.en,
  }));

  const wardOptions = [
    { value: "", label: t("searchSelectArea") },
    ...safeLocations.map((l) => ({
      value: l.name.en,
      label:
        language === "ja"
          ? l.name.ja
          : language === "ru"
            ? l.name.ru || l.name.en
            : l.name.en,
    })),
  ];

  // Sync local state when props change
  useEffect(() => {
    setLocalCriteria(criteria);
    // Find and set the matching prefecture/city codes based on criteria names
    const matchedPrefecture = safePrefectures.find(
      (p) => p.name.en === criteria.prefecture,
    );
    if (matchedPrefecture) {
      setSelectedPrefectureCode(matchedPrefecture.code);
    }
  }, [criteria, prefectures]);

  // Update city slug when cities are loaded and criteria has a city
  useEffect(() => {
    if (criteria.city && safeCities.length > 0) {
      const matchedCity = safeCities.find((c) => c.name.en === criteria.city);
      if (matchedCity) {
        setSelectedCitySlug(matchedCity.city_slug);
      }
    }
  }, [criteria.city, cities]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
        setLocalCriteria(criteria); // Reset on cancel
      }
    }

    if (isExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isExpanded, criteria]);

  // Handle prefecture change
  const handlePrefectureChange = (value: string | number) => {
    const code = Number(value);
    const selected = safePrefectures.find((p) => p.code === code);
    if (selected) {
      setLocalCriteria((prev) => ({
        ...prev,
        prefecture: selected.name.en,
        city: "",
        ward: "",
      }));
      setSelectedPrefectureCode(code);
      setSelectedCitySlug(undefined);
    }
  };

  // Handle city change
  const handleCityChange = (value: string | number) => {
    const slug = String(value);
    const selected = safeCities.find((c) => c.city_slug === slug);
    if (selected) {
      setLocalCriteria((prev) => ({
        ...prev,
        city: selected.name.en,
        ward: "",
      }));
      setSelectedCitySlug(slug);
    }
  };

  // Handle ward change
  const handleWardChange = (value: string | number) => {
    setLocalCriteria((prev) => ({
      ...prev,
      ward: String(value),
    }));
  };

  const handleApply = () => {
    onChange(localCriteria);
    setIsExpanded(false);
  };

  const handleClear = () => {
    const defaultCriteria: SearchCriteria = {
      prefecture: "",
      city: "",
      ward: "",
      moveInDate: null,
      occupants: 1,
    };
    setLocalCriteria(defaultCriteria);
    setSelectedPrefectureCode(undefined);
    setSelectedCitySlug(undefined);
  };

  // Format location for display
  const locationDisplay =
    [criteria.ward, criteria.city, criteria.prefecture]
      .filter(Boolean)
      .join(", ") || t("searchSelectArea");

  // Format date for display
  const dateDisplay = criteria.moveInDate
    ? criteria.moveInDate.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : t("searchMoveInPlaceholder");

  // Format occupants for display
  const occupantsDisplay =
    criteria.occupants === 1
      ? `1 ${t("searchPerson")}`
      : `${criteria.occupants} ${t("searchPeople")}`;

  const today = new Date();

  return (
    <Box ref={containerRef} className={`relative ${className}`}>
      {/* Collapsed View - Clickable summary bar */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="w-full bg-white rounded-2xl sm:rounded-full shadow-md border border-gray-200 px-3 sm:px-4 py-2 sm:py-2 hover:shadow-lg transition-shadow overflow-hidden"
        >
          {/* Desktop layout - single row */}
          <Box className="hidden sm:flex items-center gap-2">
            <Box className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
              {/* Location */}
              <Box className="flex items-center gap-1.5 px-3 py-1.5 border-r border-gray-200 min-w-0 shrink">
                <MapPin className="w-4 h-4 text-red-500 shrink-0" />
                <Text
                  as="span"
                  className="text-sm font-medium text-gray-900 truncate"
                >
                  {locationDisplay}
                </Text>
              </Box>

              {/* Date */}
              <Box className="flex items-center gap-1.5 px-3 py-1.5 border-r border-gray-200 shrink-0">
                <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                <Text
                  as="span"
                  className="text-sm text-gray-600 whitespace-nowrap"
                >
                  {dateDisplay}
                </Text>
              </Box>

              {/* Occupants */}
              <Box className="flex items-center gap-1.5 px-3 py-1.5 shrink-0">
                <Users className="w-4 h-4 text-gray-400 shrink-0" />
                <Text
                  as="span"
                  className="text-sm text-gray-600 whitespace-nowrap"
                >
                  {occupantsDisplay}
                </Text>
              </Box>
            </Box>

            {/* Edit indicator */}
            <Box className="flex items-center gap-2 shrink-0">
              <ChevronDown className="w-4 h-4 text-gray-400" />
              <Box className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                <Search className="w-4 h-4 text-white" />
              </Box>
            </Box>
          </Box>

          {/* Mobile layout - two rows */}
          <Box className="flex sm:hidden flex-col gap-1">
            {/* First row: Location + Search button */}
            <Box className="flex items-center justify-between">
              <Box className="flex items-center gap-1.5 min-w-0">
                <MapPin className="w-4 h-4 text-red-500 shrink-0" />
                <Text
                  as="span"
                  className="text-sm font-medium text-gray-900 truncate"
                >
                  {locationDisplay}
                </Text>
              </Box>
              <Box className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center shrink-0 ml-2">
                <Search className="w-4 h-4 text-white" />
              </Box>
            </Box>
            {/* Second row: Date + Occupants */}
            <Box className="flex items-center gap-3 text-xs text-gray-500">
              <Box className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5 shrink-0" />
                <Text as="span" className="truncate">
                  {dateDisplay}
                </Text>
              </Box>
              <Text as="span" className="text-gray-300">
                •
              </Text>
              <Box className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5 shrink-0" />
                <Text as="span">{occupantsDisplay}</Text>
              </Box>
            </Box>
          </Box>
        </button>
      )}

      {/* Expanded View - Edit form */}
      {isExpanded && (
        <Box className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4">
          {/* Header */}
          <Box className="flex items-center justify-between mb-4">
            <Text as="h3" className="font-semibold text-gray-900">
              {t("searchEditCriteria")}
            </Text>
            <button
              onClick={() => {
                setIsExpanded(false);
                setLocalCriteria(criteria);
              }}
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </Box>

          {/* Form Fields */}
          <Box className="space-y-4">
            {/* Location Row */}
            <Box className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Prefecture */}
              <Box>
                <Text
                  as="label"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("searchPrefecture")}
                </Text>
                <Select
                  value={selectedPrefectureCode || ""}
                  onChange={handlePrefectureChange}
                  options={prefectureOptions}
                  placeholder={t("searchSelectPrefecture")}
                  compact
                />
              </Box>

              {/* City */}
              <Box>
                <Text
                  as="label"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("searchCity")}
                </Text>
                <Select
                  value={selectedCitySlug || ""}
                  onChange={handleCityChange}
                  options={cityOptions}
                  placeholder={t("searchSelectCity")}
                  compact
                  disabled={!selectedPrefectureCode}
                />
              </Box>

              {/* Ward */}
              <Box>
                <Text
                  as="label"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("searchLocation")}
                </Text>
                <Select
                  value={localCriteria.ward}
                  onChange={handleWardChange}
                  options={wardOptions}
                  placeholder={t("searchSelectArea")}
                  compact
                  disabled={!selectedCitySlug}
                />
              </Box>
            </Box>

            {/* Date and Occupants Row - brick 2-col */}
            <Box className="grid grid-cols-2 gap-3">
              {/* Move-in Date */}
              <DatePicker
                label={t("searchMoveIn")}
                selected={localCriteria.moveInDate}
                onChange={(date: Date | null) =>
                  setLocalCriteria((prev) => ({
                    ...prev,
                    moveInDate: date,
                  }))
                }
                minDate={today}
                placeholderText={t("searchMoveInPlaceholder")}
                compact
              />

              {/* Occupants */}
              <Box>
                <Text
                  as="label"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("searchOccupants")}
                </Text>
                <Select
                  value={localCriteria.occupants}
                  onChange={(val) =>
                    setLocalCriteria((prev) => ({
                      ...prev,
                      occupants: Number(val),
                    }))
                  }
                  options={OCCUPANT_OPTIONS.map((opt) => ({
                    value: opt.value,
                    label:
                      opt.value === 5
                        ? `${opt.label} ${t("searchPeople")}`
                        : `${opt.label} ${opt.value === 1 ? t("searchPerson") : t("searchPeople")}`,
                  }))}
                  compact
                />
              </Box>
            </Box>
          </Box>

          {/* Actions */}
          <Box className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={handleClear}
              className="text-sm text-gray-600 hover:text-gray-900 underline"
            >
              {t("filterReset")}
            </button>
            <button
              onClick={handleApply}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              {t("searchButton")}
            </button>
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default SearchBar;
